import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {computed, makeAutoObservable, observable} from "mobx";
import {ModalType, RequestState, TeamRegion} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {IPlayerPoolStore} from "data/stores/player_pool/player_pool.store";
import type {IPlayersStore, IPlayer} from "data/stores/players/players.store";
import type {ILineup, ITeamStore} from "data/stores/team/team.store";
import type {IRoundsStore, ISession, IGolfersData} from "data/stores/rounds/rounds.store";
import {eq, get, includes, every} from "lodash";
import {ITeamResponse} from "data/providers/api/team.api.provider";
import type {IModalsStore} from "data/stores/modals/modals.store";

interface IControllerProps {
	playerId: number;
}

export interface IPlayerPoolSlotController extends ViewController<IControllerProps> {
	get i18n(): ILocalizationStore;
	get player(): IPlayer | undefined;
	get lineup(): ILineup;
	get isCurrentLineupFull(): boolean;
	get selectedSession(): ISession | undefined;
	get firstSession(): ISession | undefined;
	get golfersData(): IGolfersData | undefined;
	get selectedSessionId(): number;
	get golfersArr(): IGolfersData[];
	get partnerId(): number | undefined;
	get opponentData(): number[];
	get isTradeOpen(): boolean;
	get teamObject(): ITeamResponse;
	get tradesArr(): number[];
	get isAllTradesDone(): boolean;

	playerLastNameById: (playerId: number | undefined) => string;
	onAddPlayer: (playerId: number, isMobile: boolean) => void;
	onRemovePlayer: (playerId: number, isMobile: boolean) => void;
	scrollToTeamSelector: () => void;
	onPlayerClick: (playerId: number) => void;
}

@injectable()
export class PlayerPoolSlotController implements IPlayerPoolSlotController {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable private _playerId!: number;

	get i18n() {
		return this._i18nStore;
	}

	get teamObject() {
		return this._teamStore.teamObject;
	}

	get player() {
		return this._playerStore.getPlayerById(this._playerId);
	}

	get selectedSession() {
		return this._roundStore.selectedSession;
	}

	get firstSession() {
		return this._roundStore.firstSession;
	}

	get lineup() {
		return this._teamStore.lineup;
	}

	get isCurrentLineupFull() {
		return this._teamStore.isCurrentLineupFull;
	}

	get selectedSessionId() {
		return this._roundStore.selectedSessionId;
	}

	get golfersArr() {
		return this.selectedSession?.golfers || [];
	}

	@computed get golfersData() {
		const golfersArr = this.selectedSession?.golfers || [];

		return golfersArr.find((el) =>
			includes([...(el.usa ?? []), ...(el.europe ?? [])], this._playerId)
		);
	}

	get partnerId() {
		const usaGolfers = get(this.golfersData, "usa", []);
		const europeGolfers = get(this.golfersData, "europe", []);
		const isUsaPlayer = eq(this.player?.team, TeamRegion.Usa);

		return isUsaPlayer
			? usaGolfers.find((el) => el !== this._playerId)
			: europeGolfers.find((el) => el !== this._playerId);
	}

	get opponentData() {
		const usaGolfers = get(this.golfersData, "usa", []);
		const europeGolfers = get(this.golfersData, "europe", []);
		const isUsaPlayer = eq(this.player?.team, TeamRegion.Usa);

		return isUsaPlayer ? europeGolfers : usaGolfers;
	}

	get isTradeOpen() {
		return every([this._roundStore.isPMSession, this._teamStore.isTradeStarted]);
	}

	get tradesArr() {
		return this._teamStore.tradesArr;
	}

	get isAllTradesDone() {
		return this._teamStore.isAllTradesDone;
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.PlayerPoolStore) private _poolStore: IPlayerPoolStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	playerLastNameById = (playerId: number | undefined) => {
		if (playerId) {
			const player = this._playerStore.getPlayerById(playerId);

			return player ? player.lastName : "";
		}
		return "";
	};

	onAddPlayer = (playerId: number, isMobile: boolean) => {
		if (this.isTradeOpen) {
			this._teamStore.setPlayerIn(playerId);
		}

		this._teamStore.addPlayer(playerId);

		if (this._teamStore.isCurrentLineupFull) {
			if (isMobile) {
				this._poolStore.closePoolModal();
			}

			if (!isMobile) {
				this.scrollToTeamSelector();
			}
		}
	};

	onRemovePlayer = (playerId: number, isMobile: boolean) => {
		if (this._teamStore.isAllTradesDone) return;

		if (this.isTradeOpen) {
			this._teamStore.setPlayerOut(playerId);
		}
		this._teamStore.removePlayer(playerId);
		if (isMobile) {
			this._poolStore.closePoolModal();
		}
	};

	scrollToTeamSelector = () => {
		const temSelector = document.getElementById("team-selector");
		temSelector &&
			temSelector.scrollIntoView({
				behavior: "smooth",
				block: "center",
			});
	};

	onPlayerClick = (playerId: number) => {
		this._modalsStore.showModal(ModalType.PLAYER_PROFILE, {message: "", playerId});
	};

	dispose() {
		return;
	}

	init({playerId}: IControllerProps) {
		this._playerId = playerId;
	}
}
